import { GUIDED_OPERATION_ANCHORS, GUIDED_OPERATIONS_FIXTURE } from "../data/guidedOperations.ts";
import { responseOptions } from "../data/seed.ts";
import {
  DIVE_STAGE_BY_SCENARIO_STAGE,
  type DiveStage,
  type OperatingSnapshot,
  type OperatingSnapshotMetric,
  type ReadinessFacts,
  type ReadinessOverride,
  type ReadinessResult,
  type ReadinessRuleId,
  type ReadinessSummary,
  type SnapshotProvenanceKind,
  type SnapshotStatus,
} from "../domain/guidedOperations.ts";
import type { ScenarioStage } from "../domain/incidents.ts";

type SnapshotScenarioState = {
  stage: ScenarioStage;
  decisionId: "rollback" | "reroute" | "observe" | null;
};

const PROVENANCE_LABELS: Record<SnapshotProvenanceKind, string> = {
  "deterministic-campus-data": "Deterministic campus data",
  "current-scenario-state": "Current local scenario state",
  "derived-local-metric": "Derived locally from deterministic records",
  "demonstration-target": "Demonstration service target",
  "coming-soon-external-integration": "Coming Soon external integration",
};

function metric(
  input: Omit<OperatingSnapshotMetric, "provenance"> & {
    provenance: SnapshotProvenanceKind;
  },
): OperatingSnapshotMetric {
  return {
    ...input,
    provenance: {
      kind: input.provenance,
      label: PROVENANCE_LABELS[input.provenance],
    },
  };
}

function statusForPercentage(value: number, target: number): SnapshotStatus {
  if (value >= target) return "healthy";
  if (value >= 80) return "warning";
  return "critical";
}

export function selectDiveStage(stage: ScenarioStage): DiveStage {
  return DIVE_STAGE_BY_SCENARIO_STAGE[stage];
}

export function selectOperatingSnapshot(state: SnapshotScenarioState): OperatingSnapshot {
  const { baseline, outage, recovery } = GUIDED_OPERATIONS_FIXTURE;
  const scenarioActive = state.stage !== "calm";
  const decision = responseOptions.find((option) => option.id === state.decisionId) ?? null;
  const fullyRecovered = state.stage === "recovered";
  const serviceAvailability = fullyRecovered
    ? recovery.serviceAvailability
    : decision
      ? decision.recoveryPercent
      : scenarioActive
        ? outage.serviceAvailability
        : baseline.serviceAvailability;
  const affectedVoiceDevices = scenarioActive && !fullyRecovered
    ? outage.offlineVoiceEndpointIds.length
    : 0;
  const registeredVoiceDevices = baseline.totalVoiceDevices - affectedVoiceDevices;
  const affectedAreas = scenarioActive && !fullyRecovered ? outage.affectedAreas : [];
  const coveredBuildings = baseline.totalBuildings - affectedAreas.length;
  const observedAt = fullyRecovered
    ? recovery.checkedAt
    : scenarioActive
      ? outage.checkedAt
      : baseline.checkedAt;

  return {
    serviceSla: {
      availability: metric({
        id: "service-availability",
        label: "Service availability",
        value: serviceAvailability,
        unit: "%",
        status: statusForPercentage(serviceAvailability, baseline.serviceTarget),
        provenance: decision ? "derived-local-metric" : "current-scenario-state",
        observedAt,
        anchorIds: [GUIDED_OPERATION_ANCHORS.incidentId],
      }),
      target: metric({
        id: "service-target",
        label: "Demonstration target",
        value: baseline.serviceTarget,
        unit: "%",
        status: "neutral",
        provenance: "demonstration-target",
        observedAt: baseline.checkedAt,
        anchorIds: [GUIDED_OPERATION_ANCHORS.incidentId],
      }),
    },
    voice: {
      registered: metric({
        id: "voice-registered",
        label: "Registered desk phones",
        value: registeredVoiceDevices,
        unit: `of ${baseline.totalVoiceDevices}`,
        status: affectedVoiceDevices === 0 ? "healthy" : "warning",
        provenance: "derived-local-metric",
        observedAt,
        anchorIds: [GUIDED_OPERATION_ANCHORS.endpointId],
      }),
      affectedAnchor: metric({
        id: "voice-anchor",
        label: GUIDED_OPERATION_ANCHORS.endpointId,
        value: affectedVoiceDevices === 0 ? "Registered" : "Registration failed",
        status: affectedVoiceDevices === 0 ? "healthy" : "critical",
        provenance: "current-scenario-state",
        observedAt,
        anchorIds: [GUIDED_OPERATION_ANCHORS.endpointId, GUIDED_OPERATION_ANCHORS.dataJackId],
      }),
    },
    wifi: {
      coverage: metric({
        id: "wifi-coverage",
        label: "Buildings with full coverage",
        value: coveredBuildings,
        unit: `of ${baseline.totalBuildings}`,
        status: affectedAreas.length === 0 ? "healthy" : "warning",
        provenance: "derived-local-metric",
        observedAt,
        anchorIds: [GUIDED_OPERATION_ANCHORS.incidentId],
      }),
      affectedAreas: metric({
        id: "wifi-affected-areas",
        label: "Affected areas",
        value: affectedAreas.length === 0 ? "None" : affectedAreas.join(", "),
        status: affectedAreas.length === 0 ? "healthy" : "critical",
        provenance: "current-scenario-state",
        observedAt,
        anchorIds: [GUIDED_OPERATION_ANCHORS.incidentId, GUIDED_OPERATION_ANCHORS.switchId],
      }),
    },
    switchHealth: {
      state: metric({
        id: "switch-health",
        label: GUIDED_OPERATION_ANCHORS.switchId,
        value: scenarioActive && !fullyRecovered ? outage.switchState : baseline.switchState,
        status: scenarioActive && !fullyRecovered ? "critical" : "healthy",
        provenance: "current-scenario-state",
        observedAt,
        anchorIds: [GUIDED_OPERATION_ANCHORS.switchId, GUIDED_OPERATION_ANCHORS.changeId],
      }),
      lastCheck: metric({
        id: "switch-last-check",
        label: "Last deterministic check",
        value: observedAt,
        status: "neutral",
        provenance: "deterministic-campus-data",
        observedAt,
        anchorIds: [GUIDED_OPERATION_ANCHORS.switchId],
      }),
    },
  };
}

export function selectReadinessResults(facts: ReadinessFacts): ReadinessResult[] {
  const evidenceStatus = facts.linkedEvidenceCount >= facts.requiredEvidenceCount
    ? "pass"
    : "blocked";
  const redundancyStatus = facts.redundancyState === "available"
    ? "pass"
    : facts.redundancyState === "limited"
      ? "warning"
      : "blocked";
  const maintenanceStatus = facts.maintenanceWindow === "open"
    ? "pass"
    : facts.maintenanceWindow === "outside-window"
      ? "warning"
      : "blocked";

  return [
    {
      ruleId: "incident-acknowledged",
      label: "Incident acknowledgement",
      status: facts.acknowledgedBy ? "pass" : "blocked",
      reason: facts.acknowledgedBy
        ? `Acknowledged by ${facts.acknowledgedBy}.`
        : "A named operator has not acknowledged the incident.",
      nextAction: facts.acknowledgedBy
        ? "Continue with explicit ownership."
        : "Acknowledge INC-2048 before preparing a response.",
      canOverride: false,
    },
    {
      ruleId: "owner-assigned",
      label: "Incident owner",
      status: facts.ownerId ? "pass" : "blocked",
      reason: facts.ownerId
        ? `Ownership is assigned to ${facts.ownerId}.`
        : "No accountable owner is assigned.",
      nextAction: facts.ownerId
        ? "Keep the owner attached through recovery."
        : "Assign a named owner before requesting approval.",
      canOverride: false,
    },
    {
      ruleId: "evidence-sufficient",
      label: "Linked evidence",
      status: evidenceStatus,
      reason: evidenceStatus === "pass"
        ? `${facts.linkedEvidenceCount} evidence records satisfy the ${facts.requiredEvidenceCount}-record threshold.`
        : `${facts.linkedEvidenceCount} of ${facts.requiredEvidenceCount} required evidence records are linked.`,
      nextAction: evidenceStatus === "pass"
        ? "Review the linked evidence with the response."
        : "Link the change and switch evidence before approval.",
      canOverride: false,
    },
    {
      ruleId: "redundancy-known",
      label: "Redundancy state",
      status: redundancyStatus,
      reason: facts.redundancyState === "available"
        ? "A verified redundant path is available."
        : facts.redundancyState === "limited"
          ? "A secondary path exists, but it does not restore every affected voice endpoint."
          : facts.redundancyState === "unavailable"
            ? "No redundant path is available."
            : "The redundant path has not been verified.",
      nextAction: redundancyStatus === "pass"
        ? "Include the verified path in the response review."
        : redundancyStatus === "warning"
          ? "Record why limited redundancy is acceptable or choose a safer response."
          : "Verify or establish a safe path before approval.",
      canOverride: redundancyStatus === "warning",
    },
    {
      ruleId: "maintenance-window",
      label: "Maintenance window",
      status: maintenanceStatus,
      reason: facts.maintenanceWindow === "open"
        ? "The controlled change is inside the approved window."
        : facts.maintenanceWindow === "outside-window"
          ? "Finals-week response work is outside the routine maintenance window."
          : "The maintenance-window state is unknown.",
      nextAction: maintenanceStatus === "pass"
        ? "Proceed to human approval."
        : maintenanceStatus === "warning"
          ? "Record an emergency-change override with a plain-language reason."
          : "Confirm the applicable window before approval.",
      canOverride: maintenanceStatus === "warning",
    },
    {
      ruleId: "human-approver",
      label: "Human approver",
      status: facts.approver?.canApproveSensitiveChanges ? "pass" : "blocked",
      reason: !facts.approver
        ? "No human approver is named."
        : facts.approver.canApproveSensitiveChanges
          ? `${facts.approver.name} is named and has approval permission.`
          : `${facts.approver.name} is named but cannot approve sensitive changes.`,
      nextAction: facts.approver?.canApproveSensitiveChanges
        ? "Present approve and reject choices without a preselected outcome."
        : "Select a Department Lead with approval permission.",
      canOverride: false,
    },
  ];
}

export function summarizeReadiness(
  results: readonly ReadinessResult[],
  overrides: readonly ReadinessOverride[],
): ReadinessSummary {
  const overriddenRuleIds = new Set<ReadinessRuleId>(
    overrides.map((override) => override.ruleId),
  );
  const blockedCount = results.filter((result) => result.status === "blocked").length;
  const warningResults = results.filter((result) => result.status === "warning");
  const unresolvedWarningCount = warningResults.filter(
    (result) => !result.canOverride || !overriddenRuleIds.has(result.ruleId),
  ).length;

  return {
    results,
    blockedCount,
    warningCount: warningResults.length,
    unresolvedWarningCount,
    confirmable: blockedCount === 0 && unresolvedWarningCount === 0,
  };
}
